import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Plus, Clock, CheckCircle2, Lock, X, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { getGuestId } from "../lib/guest";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { track } from "../lib/analytics";
import { toast } from "sonner";

export default function Projects() {
  const { t } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [meta, setMeta] = useState({ live: [], coming_soon: [] });
  const [mine, setMine] = useState([]);
  const [soon, setSoon] = useState(null);

  const loadMine = () => {
    const gid = getGuestId();
    api.get(`/projects?guest_id=${gid}`).then((r) => setMine(r.data)).catch(() => setMine([]));
  };
  useEffect(() => { api.get("/projects/meta/goals").then((r) => setMeta(r.data)).catch(() => {}); loadMine(); /* eslint-disable-next-line */ }, [user]);

  const startLive = async (goal_type, title) => {
    try {
      const { data } = await api.post("/projects", { goal: `I want to ${title.toLowerCase()}`, goal_type, guest_id: getGuestId() });
      track("project_created", { goal_type });
      nav(`/projects/${data.id}`);
    } catch (e) {
      toast.error("Could not start the project. Please try again.");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14" data-testid="projects-page">
      <div className="text-xs uppercase tracking-[0.2em] text-gold/70">{t("projects.eyebrow")}</div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-2">{t("projects.title")}</h1>
      <p className="text-white/60 mt-3 max-w-2xl">{t("projects.sub")}</p>

      {/* My projects */}
      {mine.length > 0 && (
        <div className="mt-12">
          <div className="text-sm font-semibold text-white/80 mb-3">Your projects</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {mine.map((p) => {
              const total = p.steps?.length || 0;
              const done = (p.steps || []).filter((s) => s.status === "done" || s.status === "handed_off").length;
              return (
                <button key={p.id} onClick={() => nav(`/projects/${p.id}`)} data-testid="my-project-card"
                  className="text-left rounded-2xl bg-surface/60 border border-white/[0.06] p-6 hover:border-gold/30 hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">{p.status}</span>
                    <span className="text-[11px] text-white/40">{done}/{total} steps</span>
                  </div>
                  <div className="font-display text-xl text-white mt-3">{p.title}</div>
                  <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-gold rounded-full transition-all" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
                  </div>
                  <div className="mt-4 flex items-center gap-1 text-gold text-sm">Open workspace <ArrowRight size={14} /></div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Start a goal */}
      <div className="mt-14">
        <div className="text-sm font-semibold text-white/80 mb-3">Start with a goal</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {meta.live.map((g) => (
            <div key={g.goal_type} data-testid={`goal-live-${g.goal_type}`}
              className="rounded-2xl bg-surface/60 border border-gold/20 p-6 hover:border-gold/40 transition-all duration-300">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/20"><Sparkles size={11} /> Live</span>
              <div className="font-display text-xl text-white mt-3">{g.title}</div>
              <ul className="mt-3 space-y-1.5">{g.steps.map((s) => <li key={s} className="text-xs text-white/55 flex gap-2"><span className="text-gold">›</span>{s}</li>)}</ul>
              <button onClick={() => startLive(g.goal_type, g.title)} data-testid="start-live-project"
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gold text-ink text-sm font-semibold hover:brightness-110 transition-all">
                <Plus size={14} /> Track this as a Project
              </button>
            </div>
          ))}
          {meta.coming_soon.map((g) => (
            <button key={g.goal_type} onClick={() => setSoon(g)} data-testid={`goal-soon-${g.goal_type}`}
              className="text-left rounded-2xl bg-surface/40 border border-white/[0.06] p-6 hover:border-white/20 transition-all duration-300 relative">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-white/50 border border-white/10"><Clock size={11} /> Coming soon</span>
              <div className="font-display text-xl text-white/80 mt-3">{g.title}</div>
              <div className="mt-3 flex items-center gap-1 text-white/50 text-sm"><Lock size={13} /> Join the waitlist</div>
            </button>
          ))}
        </div>
      </div>

      {soon && <ComingSoonModal goal={soon} onClose={() => setSoon(null)} />}
    </div>
  );
}

const ComingSoonModal = ({ goal, onClose }) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const join = async (e) => {
    e.preventDefault();
    try {
      await api.post("/waitlist", { email, capability: goal.goal_type });
      setDone(true);
      track("waitlist_joined", { source: "projects", capability: goal.goal_type });
    } catch { toast.error("Please try again."); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="coming-soon-modal">
      <div className="w-full max-w-md rounded-2xl bg-surface border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-white font-semibold">{goal.title}</div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="coming-soon-close"><X size={18} /></button>
        </div>
        <p className="text-sm text-white/60 mt-3">
          {goal.title} isn't live yet. Property is our first fully-working vertical — every other goal is honestly gated until its real integrations and licensed data are in place. Join the waitlist and we'll email you the moment it goes live. No fake flows.
        </p>
        {done ? (
          <p className="text-emerald-400 text-sm mt-4 flex items-center gap-2"><CheckCircle2 size={16} /> You're on the list.</p>
        ) : (
          <form onSubmit={join} className="mt-4 flex gap-2">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="coming-soon-email"
              className="flex-1 bg-input/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40" />
            <button type="submit" data-testid="coming-soon-join" className="px-3 py-2 rounded-lg bg-gold text-ink text-sm font-semibold hover:brightness-110 transition-all">Join</button>
          </form>
        )}
      </div>
    </div>
  );
};
